import { useState } from 'react';
import { ApiError } from '../../lib/api';
import { estimateChapterCount, fetchRoomChapters, fetchRoomDescription, roomExportCsvUrl } from '../../lib/exports';
import { pushToast } from '../../lib/events';
import type { SectionTimestampEntry } from '../../lib/rooms';
import type { Template } from '../../lib/templates';

interface ExportMenuProps {
  roomId: number;
  template: Template;
  streamStartedAt: string | null;
  sectionTimestamps: SectionTimestampEntry[];
  /** Compact styling for the FINISHED `StatusBand` instance — smaller type/padding, same actions. */
  compact?: boolean;
}

const BUTTON_BASE =
  'min-h-tap rounded-md border border-border-strong font-semibold text-text-muted transition-colors hover:border-accent-border hover:text-text disabled:cursor-not-allowed disabled:opacity-50 disabled:hover:border-border-strong disabled:hover:text-text-muted';

const LINK_BASE =
  'min-h-tap inline-flex items-center rounded-md border border-accent-border bg-accent-dim font-semibold text-accent transition-colors hover:bg-accent hover:text-accent-contrast';

function buttonClass(compact: boolean): string {
  return `${BUTTON_BASE} ${compact ? 'px-2 text-xs' : 'px-3 text-sm'}`;
}

function linkClass(compact: boolean): string {
  return `${LINK_BASE} ${compact ? 'px-2 text-xs' : 'px-3 text-sm'}`;
}

/**
 * Fetches `load`'s text and copies it to the clipboard. The two failure modes get different
 * messages: a fetch failure surfaces the server's own message for a `409` (e.g. `no_stream_start`'s
 * "Mark the stream start before exporting chapters") rather than a generic one, falling back to
 * `loadFailureMessage` for anything else; a clipboard-write failure (permission denied, insecure
 * context, …) always gets the generic "Could not copy" toast since there's no server message for it.
 */
async function copyText(
  load: () => Promise<string>,
  successMessage: string,
  loadFailureMessage: string,
): Promise<void> {
  let text: string;
  try {
    text = await load();
  } catch (error) {
    pushToast(error instanceof ApiError && error.status === 409 ? error.serverMessage : loadFailureMessage);
    return;
  }
  try {
    await navigator.clipboard.writeText(text);
  } catch {
    pushToast('Could not copy to the clipboard.');
    return;
  }
  pushToast(successMessage, 'success');
}

/**
 * Room header export controls (VIEW — any room member, per `docs/specs/WP8-exports.md`): a plain
 * same-origin link for the CSV (the browser follows the server's `Content-Disposition`) and two
 * clipboard actions. Chapters/description both need `streamStartedAt` set (the backend answers
 * `409 no_stream_start` otherwise); the chapter count additionally needs to reach 3 for YouTube's
 * own minimum before "Copy chapters" is offered — each disabled state names its reason inline
 * rather than only in a hover title, so it reads on a touch device too.
 */
export function ExportMenu({ roomId, template, streamStartedAt, sectionTimestamps, compact = false }: ExportMenuProps) {
  const [pending, setPending] = useState<'chapters' | 'description' | null>(null);

  const noStreamStart = streamStartedAt === null;
  const chapterCount = estimateChapterCount(template, sectionTimestamps, streamStartedAt);
  const tooFewChapters = !noStreamStart && chapterCount < 3;

  const chaptersReason = noStreamStart
    ? 'Mark stream start first'
    : tooFewChapters
      ? 'Fewer than 3 chapters — YouTube needs at least 3'
      : null;
  const descriptionReason = noStreamStart ? 'Mark stream start first' : null;

  async function onCopyChapters() {
    setPending('chapters');
    await copyText(() => fetchRoomChapters(roomId), 'Chapters copied.', 'Could not copy the chapters.');
    setPending(null);
  }

  async function onCopyDescription() {
    setPending('description');
    await copyText(() => fetchRoomDescription(roomId), 'Description copied.', 'Could not copy the description.');
    setPending(null);
  }

  return (
    <div
      role="group"
      aria-label="Export"
      className={`flex flex-wrap items-center ${compact ? 'gap-x-2 gap-y-1' : 'gap-x-3 gap-y-2'}`}
    >
      <a href={roomExportCsvUrl(roomId)} download className={linkClass(compact)}>
        Download CSV
      </a>

      <div className="flex flex-col gap-1">
        <button
          type="button"
          disabled={chaptersReason !== null || pending === 'chapters'}
          onClick={() => void onCopyChapters()}
          className={buttonClass(compact)}
        >
          Copy chapters
        </button>
        {chaptersReason && <span className="telemetry text-text-faint">{chaptersReason}</span>}
      </div>

      <div className="flex flex-col gap-1">
        <button
          type="button"
          disabled={descriptionReason !== null || pending === 'description'}
          onClick={() => void onCopyDescription()}
          className={buttonClass(compact)}
        >
          Copy description
        </button>
        {descriptionReason && <span className="telemetry text-text-faint">{descriptionReason}</span>}
      </div>
    </div>
  );
}
